import type { ReactNode } from "react";
import { IconButton, RotateCcwIcon } from "@carbon6/c6-frontend-design";
import { FilterResultChip } from "./FilterResultChip";
import styles from "./filterAnatomy.module.css";

export type FilterBarChip = {
  key: string;
  label: string;
  removeLabel: string;
  onRemove: () => void;
};

type FilterBarProps = {
  ariaLabel: string;
  /** 행 1 머리의 검색 컨트롤. 보통 `FilterSearchInput`. */
  search?: ReactNode;
  /** 행 1 의 나머지 축 컨트롤(드롭다운 · 스위치). size 를 지정하지 않아 c6 기본(md)을 쓴다. */
  children?: ReactNode;
  /** 행 2 의 적용 조건. 비어 있으면 행 2 자체를 그리지 않는다. */
  chips?: readonly FilterBarChip[];
  onClearAll?: () => void;
};

/**
 * 목록 화면의 조회 축 셸.
 *
 * 페이지는 컨트롤의 종류를 고르지만 자리 · 크기 · 칩 · 초기화 배치는 바꾸지 않는다 —
 * 목록 화면들이 한 해부학으로 읽히게 하는 것이 이 셸의 일이다(규칙 §13).
 *
 * Owns:
 * - 두 행의 배치, 적용 조건 칩 줄, 초기화 버튼의 노출 조건.
 *
 * Does not own:
 * - 축의 의미 · 저장 위치 · 칩 문구 — 호출부가 만들어 넘긴다.
 */
export function FilterBar({ ariaLabel, children, chips, onClearAll, search }: FilterBarProps) {
  const hasChips = chips !== undefined && chips.length > 0;

  return (
    <section aria-label={ariaLabel} className={styles.bar}>
      <div className={styles.row}>
        {search}
        {children}
      </div>
      {hasChips ? (
        <div className={`${styles.row} ${styles.chipRow}`}>
          <div className={styles.chips}>
            {chips.map((chip) => (
              <FilterResultChip
                key={chip.key}
                label={chip.label}
                removeLabel={chip.removeLabel}
                onRemove={chip.onRemove}
              />
            ))}
          </div>
          {onClearAll === undefined ? null : (
            <IconButton size="sm" text="초기화" variant="ghost" onClick={onClearAll}>
              <RotateCcwIcon />
            </IconButton>
          )}
        </div>
      ) : null}
    </section>
  );
}
